/**
 * SaaS Billing Dashboard (Super Admin)
 * Dashboard completo: KPIs, receita, grafico, vencimentos, pagamentos, signups
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, Button, useToast, ConfirmDialog } from "@payflow/ui";

const API_BASE = (import.meta as any).env?.VITE_API_URL || "http://localhost:8787";
const TOKEN_KEY = "payflow_token";

interface SaasDashboardData {
  mrr: number;
  arr: number;
  totalTenants: number;
  activeTenants: number;
  suspendedTenants: number;
  trialTenants: number;
  cancelledTenants: number;
  pendingSignups: number;
  tenantsByPlan: { plan: string; count: number }[];
  revenueThisMonth: number;
  revenueLastMonth: number;
  revenueGrowth: number;
  revenueLast6Months: { month: string; revenue: number }[];
  overdueSubscriptions: number;
  paidSubscriptionsThisMonth: number;
  upcomingBillings: { tenantId: string; tenantNome: string; plan: string; amount: number; dueDate: string }[];
  recentPayments: { tenantId: string; tenantNome: string; amount: string; method: string; paidAt: string; referenceMonth: string }[];
  pendingSignupsList: { id: string; nomeEmpresa: string; cnpj: string; email: string; plan: string; status: string; createdAt: string }[];
  newTenantsThisMonth: number;
  newTenantsLastMonth: number;
}

export function BillingDashboardPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState<SaasDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expiring, setExpiring] = useState<string | null>(null);
  const [expireTarget, setExpireTarget] = useState<{ id: string; nome: string } | null>(null);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const res = await fetch(`${API_BASE}/v1/admin/billing/saas-dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success) setData(json.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExpireSignup = async () => {
    if (!expireTarget) return;
    setExpiring(expireTarget.id);
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const res = await fetch(`${API_BASE}/v1/admin/billing/signups/${expireTarget.id}/expire`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success) {
        toast({ variant: "success", title: "Signup expirado com sucesso" });
        fetchData();
      } else {
        toast({ variant: "error", title: "Erro ao expirar signup" });
      }
    } catch {
      toast({ variant: "error", title: "Erro de conexao" });
    } finally {
      setExpiring(null);
      setExpireTarget(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">Carregando...</div>;
  }

  if (!data) {
    return <div className="py-12 text-center text-red-500">Erro ao carregar dados de billing</div>;
  }

  const maxRevenue = Math.max(...data.revenueLast6Months.map((m) => m.revenue), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Dashboard SaaS</h1>
        <Button onClick={() => navigate("/gestor/tenants")}>
          Ver todos os tenants
        </Button>
      </div>

      {/* Linha 1 - KPIs */}
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        <KpiCard label="MRR" value={formatCurrency(data.mrr)} />
        <KpiCard label="ARR" value={formatCurrency(data.arr)} />
        <KpiCard label="Ativos" value={String(data.activeTenants)} variant="success" />
        <KpiCard label="Suspensos" value={String(data.suspendedTenants)} variant={data.suspendedTenants > 0 ? "danger" : "default"} />
        <KpiCard label="Trial" value={String(data.trialTenants)} />
        <KpiCard label="Novos este mes" value={String(data.newTenantsThisMonth)} subtext={`${data.newTenantsLastMonth} mes anterior`} />
      </div>

      {/* Linha 2 - Receita + Distribuicao por plano */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-muted-foreground">Receita</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Este mes</p>
                <p className="text-2xl font-bold text-foreground">{formatCurrency(data.revenueThisMonth)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Mes anterior</p>
                <p className="text-lg text-muted-foreground">{formatCurrency(data.revenueLastMonth)}</p>
              </div>
              <div className="ml-auto">
                <GrowthBadge value={data.revenueGrowth} />
              </div>
            </div>
            <div className="mt-3 flex gap-4 text-xs text-muted-foreground">
              <span>Pagos este mes: {data.paidSubscriptionsThisMonth}</span>
              {data.overdueSubscriptions > 0 && (
                <span className="text-red-500">Inadimplentes: {data.overdueSubscriptions}</span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-muted-foreground">Distribuicao por Plano</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.tenantsByPlan.map((p) => (
                <div key={p.plan} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block h-3 w-3 rounded-full ${p.plan === "completo" ? "bg-primary-500" : "bg-primary-300"}`} />
                    <span className="text-sm font-medium text-foreground">{planLabel(p.plan)}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">{p.count} tenant{p.count !== 1 ? "s" : ""}</span>
                </div>
              ))}
              {data.tenantsByPlan.length === 0 && (
                <p className="text-sm text-muted-foreground">Nenhum tenant cadastrado</p>
              )}
              <div className="border-t border-border pt-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-foreground">Total</span>
                  <span className="text-muted-foreground">{data.totalTenants} tenant{data.totalTenants !== 1 ? "s" : ""}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Linha 3 - Grafico de barras (CSS) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm text-muted-foreground">Receita - Ultimos 6 meses</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-2" style={{ height: 180 }}>
            {data.revenueLast6Months.map((m) => {
              const height = maxRevenue > 0 ? (m.revenue / maxRevenue) * 100 : 0;
              return (
                <div key={m.month} className="flex flex-1 flex-col items-center gap-1">
                  <span className="text-xs text-muted-foreground">{formatCurrency(m.revenue)}</span>
                  <div
                    className="w-full rounded-t bg-primary-500 transition-all"
                    style={{ height: `${Math.max(height, 2)}%`, minHeight: 4 }}
                  />
                  <span className="text-xs text-muted-foreground">{formatMonthLabel(m.month)}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Linha 4 - Vencimentos + Pagamentos recentes */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Proximos vencimentos */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-muted-foreground">Proximos vencimentos (7 dias)</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">Tenant</th>
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">Plano</th>
                    <th className="px-4 py-2 text-right font-medium text-muted-foreground">Valor</th>
                    <th className="px-4 py-2 text-right font-medium text-muted-foreground">Vencimento</th>
                  </tr>
                </thead>
                <tbody>
                  {data.upcomingBillings.length === 0 ? (
                    <tr><td colSpan={4} className="px-4 py-4 text-center text-muted-foreground">Nenhum vencimento proximo</td></tr>
                  ) : data.upcomingBillings.map((b, i) => (
                    <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-2 font-medium text-foreground">{b.tenantNome}</td>
                      <td className="px-4 py-2">
                        <PlanBadge plan={b.plan} />
                      </td>
                      <td className="px-4 py-2 text-right text-foreground">{formatCurrency(b.amount)}</td>
                      <td className="px-4 py-2 text-right text-muted-foreground">{formatDateShort(b.dueDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pagamentos recentes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-muted-foreground">Pagamentos recentes</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">Tenant</th>
                    <th className="px-4 py-2 text-right font-medium text-muted-foreground">Valor</th>
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">Metodo</th>
                    <th className="px-4 py-2 text-right font-medium text-muted-foreground">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentPayments.length === 0 ? (
                    <tr><td colSpan={4} className="px-4 py-4 text-center text-muted-foreground">Nenhum pagamento recente</td></tr>
                  ) : data.recentPayments.map((p, i) => (
                    <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-2 font-medium text-foreground">{p.tenantNome}</td>
                      <td className="px-4 py-2 text-right text-foreground">{formatCurrency(Number(p.amount))}</td>
                      <td className="px-4 py-2">
                        <span className={`rounded px-2 py-0.5 text-xs font-medium ${p.method === "pix" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                          {p.method === "pix" ? "PIX" : "Manual"}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right text-muted-foreground">{formatDateTime(p.paidAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Linha 5 - Signups pendentes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm text-muted-foreground">
            Signups pendentes ({data.pendingSignups})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">Empresa</th>
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">CNPJ</th>
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">Email</th>
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">Plano</th>
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">Data</th>
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">Acoes</th>
                </tr>
              </thead>
              <tbody>
                {data.pendingSignupsList.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-4 text-center text-muted-foreground">Nenhum signup pendente</td></tr>
                ) : data.pendingSignupsList.map((s) => (
                  <tr key={s.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-2 font-medium text-foreground">{s.nomeEmpresa}</td>
                    <td className="px-4 py-2 text-muted-foreground">{s.cnpj}</td>
                    <td className="px-4 py-2 text-muted-foreground">{s.email}</td>
                    <td className="px-4 py-2">
                      <PlanBadge plan={s.plan} />
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{formatDateTime(s.createdAt)}</td>
                    <td className="px-4 py-2">
                      {s.status === "awaiting_payment" && (
                        <Button
                          variant="destructive"
                          size="sm"
                          loading={expiring === s.id}
                          onClick={() => setExpireTarget({ id: s.id, nome: s.nomeEmpresa })}
                        >
                          Expirar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Confirm Expire */}
      <ConfirmDialog
        open={!!expireTarget}
        onOpenChange={(open) => !open && setExpireTarget(null)}
        title="Expirar signup"
        description={`Tem certeza que deseja expirar o signup de "${expireTarget?.nome}"?`}
        confirmLabel="Expirar"
        cancelLabel="Cancelar"
        variant="warning"
        loading={!!expiring}
        onConfirm={handleExpireSignup}
      />
    </div>
  );
}

// ========== Helper Components ==========

function KpiCard({ label, value, subtext, variant = "default" }: {
  label: string;
  value: string;
  subtext?: string;
  variant?: "default" | "success" | "danger";
}) {
  const colorClass = variant === "danger" ? "text-red-500" : variant === "success" ? "text-green-600" : "text-foreground";
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`mt-1 text-xl font-bold ${colorClass}`}>{value}</p>
        {subtext && <p className="mt-0.5 text-xs text-muted-foreground">{subtext}</p>}
      </CardContent>
    </Card>
  );
}

function GrowthBadge({ value }: { value: number }) {
  if (value === 0) return <span className="text-sm text-muted-foreground">0%</span>;
  const isPositive = value > 0;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-sm font-medium ${isPositive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
      {isPositive ? "\u2191" : "\u2193"} {Math.abs(value).toFixed(1)}%
    </span>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  return (
    <span className="rounded bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
      {planLabel(plan)}
    </span>
  );
}

// ========== Helpers ==========

function planLabel(p: string) {
  switch (p) {
    case "web": return "Web";
    case "completo": return "Completo";
    default: return p;
  }
}

function formatCurrency(value: number) {
  return `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
}

function formatDateShort(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function formatMonthLabel(month: string) {
  const [year, m] = month.split("-");
  const months = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  return `${months[Number(m) - 1]}/${year.slice(2)}`;
}
